import SaveOutlined from '@ant-design/icons/lib/icons/SaveOutlined';
import { Col, Row } from 'antd';
import Button from 'components/ui/base/button';
import CustomModal from 'components/ui/base/modal/Modal';
import { H5, Text } from 'components/ui/base/typography';
import FormInput from 'components/ui/composed/declarations/formInput/DeclarationInput';
import { useFormik } from 'formik';
import useCustomers from 'hooks/useCustomers';
import useSession from 'hooks/useSession';
import { FC, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { NotifyEmails } from 'store/declarations/declaration';
import useDeclarations from '../../../../hooks/useDeclarations';
import useGlobalOverlay from '../../../../hooks/useGlobalOverlay';
import { CheckboxDiv, EditIcon, InfoDiv, Label, MailIcon, SaveIcon, StyledCheckBox } from './SubmitModal.styles';
import Notification from 'components/ui/base/notification/Notification';
import { getErrorMessage } from 'utils/getErrorMessage';

interface Props {
    customerId: string | undefined;
    visible: boolean;
    handleCancel: () => void;
    onSuccess: () => void;
    applySuggestedGrossMass: boolean;
}

const SubmitModal: FC<Props> = ({ customerId, visible, handleCancel, onSuccess, applySuggestedGrossMass }) => {
    const { userInfo } = useSession();
    const { declarationId } = useParams<{ declarationId: string }>();
    const { submitDeclaration } = useDeclarations();
    const { hideGlobalOverlay, showGlobalOverlay } = useGlobalOverlay();

    const { customer } = useCustomers({ customerId });
    const [editCustomerEmail, setEditCustomerEmail] = useState<boolean>(false);
    const [editDeclarantEmail, setEditDeclarantEmail] = useState<boolean>(false);

    const submitDeclarationRequest = async (emails?: NotifyEmails): Promise<boolean> => {
        if (!declarationId) return false;

        showGlobalOverlay({ type: 'LoadingOverlay', payload: { message: 'Submitting declaration...' } });
        try {
            await submitDeclaration(declarationId, { ...emails }, applySuggestedGrossMass);
            return true;
        } catch (err) {
            Notification({
                type: 'error',
                messageTitle: 'Submission Error',
                description: getErrorMessage(err) || 'Could not submit declaration. Please try again.',
                placement: 'top',
            });
            return false;
        } finally {
            hideGlobalOverlay();
        }
    };

    const handleSubmitOnly = async () => {
        const ok = await submitDeclarationRequest();
        if (ok) onSuccess();
    };

    const handleSubmitAndNotify = async () => {
        const emails: NotifyEmails = {};

        if (formik.values.customer) emails.customerEmail = formik.values.customerEmail;
        if (formik.values.declarant) emails.clerkEmail = formik.values.declarantEmail;

        const ok = await submitDeclarationRequest(emails);
        if (ok) onSuccess();
    };

    useEffect(() => {
        if (userInfo && userInfo.email) {
            formik.setFieldValue('declarantEmail', userInfo.email);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userInfo]);

    useEffect(() => {
        if (customer && customer.contact?.email) {
            formik.setFieldValue('customerEmail', customer.contact.email);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customer]);

    const formik = useFormik<{
        customer: boolean;
        declarant: boolean;
        customerEmail?: string;
        declarantEmail?: string;
    }>({
        initialValues: {
            customer: false,
            declarant: false,
            declarantEmail: userInfo?.email,
            customerEmail: customer?.contact?.email,
        },
        validateOnMount: true,
        enableReinitialize: true,
        onSubmit: () => undefined,
    });

    const updateCustomerEmail = () => {
        setEditCustomerEmail(false);
    };

    const updateDeclarantEmail = () => {
        setEditDeclarantEmail(false);
    };

    const enableNotifyButton = formik.values.customer || formik.values.declarant;

    return (
        <CustomModal
            data-testid="declaration-confirmation-submit-modal"
            title={<H5>Confirm declaration submit</H5>}
            centered
            open={visible}
            onCancel={handleCancel}
            width={676}
            equalButtons
            footer={[
                <Button
                    data-testid="declaration-confirmation-submit-button"
                    onClick={() => handleSubmitOnly()}
                    size="large"
                >
                    Submit
                </Button>,
                <Button
                    data-testid="declaration-confirmation-submit-and-notify-button"
                    disabled={!enableNotifyButton}
                    onClick={() => handleSubmitAndNotify()}
                    size="large"
                >
                    <Text>
                        Submit and notify <MailIcon />
                    </Text>
                </Button>,
            ]}
        >
            {customer && userInfo && (
                <>
                    <InfoDiv>
                        <Text>
                            To notify your customer and declarant about changes on declaration/routing status, please
                            select and press notify. If you dont want just hit skip.
                        </Text>
                    </InfoDiv>
                    <CheckboxDiv>
                        {!editCustomerEmail ? (
                            <Row align="middle">
                                <StyledCheckBox
                                    checked={formik.getFieldProps('customer').value as boolean}
                                    onChange={() => {
                                        const values = { ...formik.values };
                                        values.customer = !formik.values.customer;
                                        formik.setValues(values);
                                    }}
                                />
                                <Label>Customer email:</Label>
                                <Text>{formik.values.customerEmail}</Text>
                                <EditIcon onClick={() => setEditCustomerEmail(true)} />
                            </Row>
                        ) : (
                            <Row align="middle">
                                <Col flex={4}>
                                    <FormInput
                                        fieldProps={formik.getFieldProps('customerEmail')}
                                        fieldHelper={formik.getFieldHelpers('customerEmail')}
                                        label="Customer email"
                                        icon
                                    />
                                </Col>

                                <Col flex={1}>
                                    <SaveIcon onClick={() => updateCustomerEmail()}>
                                        save <SaveOutlined />
                                    </SaveIcon>
                                </Col>
                            </Row>
                        )}
                    </CheckboxDiv>
                    <CheckboxDiv>
                        {!editDeclarantEmail ? (
                            <Row align="middle">
                                <StyledCheckBox
                                    checked={formik.getFieldProps('declarant').value as boolean}
                                    onChange={() => {
                                        const values = { ...formik.values };
                                        values.declarant = !formik.values.declarant;
                                        formik.setValues(values);
                                    }}
                                />
                                <Label>Declarant email:</Label>
                                <Text>{formik.values.declarantEmail}</Text>
                                <EditIcon onClick={() => setEditDeclarantEmail(true)} />
                            </Row>
                        ) : (
                            <Row align="middle">
                                <Col flex={4}>
                                    <FormInput
                                        fieldProps={formik.getFieldProps('declarantEmail')}
                                        fieldHelper={formik.getFieldHelpers('declarantEmail')}
                                        label="Declarant email"
                                        icon
                                    />
                                </Col>

                                <Col flex={1}>
                                    <SaveIcon onClick={() => updateDeclarantEmail()}>
                                        save <SaveOutlined />
                                    </SaveIcon>
                                </Col>
                            </Row>
                        )}
                    </CheckboxDiv>
                </>
            )}
        </CustomModal>
    );
};
export default SubmitModal;
